import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { logger } from '@/infra/logger/Logger';

/**
 * Supabase wrapper. Sem URL/key configurados, retorna `null` em isAvailable() e
 * todos os métodos de IO viram no-ops.
 */

export interface CloudUser {
  id: string;
  email: string | null;
}

export interface CloudSavePayload {
  payload: unknown;       // JSON (SaveV6)
  updatedAt: number;      // ms epoch
  schemaMinor: number;
}

export interface CloudClientApi {
  isAvailable(): boolean;
  getUser(): Promise<CloudUser | null>;
  signInWithEmail(email: string, redirectTo?: string): Promise<{ ok: boolean; error?: string }>;
  signOut(): Promise<void>;
  fetchSave(userId: string): Promise<CloudSavePayload | null>;
  upsertSave(userId: string, save: CloudSavePayload): Promise<{ ok: boolean; error?: string }>;
  onAuthStateChange(callback: (user: CloudUser | null) => void): () => void;
}

const URL_ENV = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const KEY_ENV = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
const TABLE = 'saves';

class CloudClientImpl implements CloudClientApi {
  private client: SupabaseClient | null = null;

  constructor() {
    if (URL_ENV && KEY_ENV) {
      try {
        this.client = createClient(URL_ENV, KEY_ENV, {
          auth: { persistSession: true, autoRefreshToken: true },
        });
        logger.info('cloud.client.initialized');
      } catch (err) {
        logger.error('cloud.client.init-failed', err as Error);
      }
    } else {
      logger.info('cloud.client.disabled', { reason: 'env-missing' });
    }
  }

  isAvailable(): boolean {
    return this.client !== null;
  }

  async getUser(): Promise<CloudUser | null> {
    if (!this.client) return null;
    try {
      const { data } = await this.client.auth.getUser();
      if (!data.user) return null;
      return { id: data.user.id, email: data.user.email ?? null };
    } catch (err) {
      logger.warn('cloud.getUser.failed', { err: String(err) });
      return null;
    }
  }

  async signInWithEmail(
    email: string,
    redirectTo?: string,
  ): Promise<{ ok: boolean; error?: string }> {
    if (!this.client) return { ok: false, error: 'cloud-disabled' };
    try {
      const opts = redirectTo ? { options: { emailRedirectTo: redirectTo } } : {};
      const { error } = await this.client.auth.signInWithOtp({ email, ...opts });
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    } catch (err) {
      return { ok: false, error: String(err) };
    }
  }

  async signOut(): Promise<void> {
    if (!this.client) return;
    try {
      await this.client.auth.signOut();
    } catch (err) {
      logger.warn('cloud.signOut.failed', { err: String(err) });
    }
  }

  async fetchSave(userId: string): Promise<CloudSavePayload | null> {
    if (!this.client) return null;
    try {
      const { data, error } = await this.client
        .from(TABLE)
        .select('payload, updated_at, schema_minor')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) {
        logger.warn('cloud.fetchSave.error', { err: error.message });
        return null;
      }
      if (!data) return null;
      return {
        payload: data.payload,
        updatedAt: new Date(data.updated_at as string).getTime(),
        schemaMinor: data.schema_minor as number,
      };
    } catch (err) {
      logger.warn('cloud.fetchSave.failed', { err: String(err) });
      return null;
    }
  }

  async upsertSave(
    userId: string,
    save: CloudSavePayload,
  ): Promise<{ ok: boolean; error?: string }> {
    if (!this.client) return { ok: false, error: 'cloud-disabled' };
    try {
      const { error } = await this.client.from(TABLE).upsert(
        {
          user_id: userId,
          payload: save.payload,
          updated_at: new Date(save.updatedAt).toISOString(),
          schema_minor: save.schemaMinor,
        },
        { onConflict: 'user_id' },
      );
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    } catch (err) {
      return { ok: false, error: String(err) };
    }
  }

  onAuthStateChange(callback: (user: CloudUser | null) => void): () => void {
    if (!this.client) return () => {};
    const sub = this.client.auth.onAuthStateChange((_event, session) => {
      const user = session?.user
        ? { id: session.user.id, email: session.user.email ?? null }
        : null;
      callback(user);
    });
    return () => sub.data.subscription.unsubscribe();
  }
}

let _instance: CloudClientApi | null = null;

export function getCloudClient(): CloudClientApi {
  if (!_instance) _instance = new CloudClientImpl();
  return _instance;
}

export function setCloudClientForTests(impl: CloudClientApi): void {
  _instance = impl;
}

export function resetCloudClientForTests(): void {
  _instance = null;
}
